import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { aiStudies, aiCareers, type AiTrack } from '@/content/aiFuture';

type TrackId = 'studies' | 'careers';

function TrackView({ data, hidden }: { data: AiTrack; hidden: boolean }) {
  return (
    <div hidden={hidden} className="mt-10">
      <p className="text-lg text-ink-700 max-w-3xl leading-relaxed">{data.intro}</p>

      {/* Themes */}
      <div className="mt-8 grid gap-4 md:grid-cols-2">
        {data.themes.map((t) => (
          <div key={t.heading} className="aces-card p-5 sm:p-6">
            <h3 className="font-serif text-lg text-ink-900 leading-snug">{t.heading}</h3>
            <p className="text-sm text-ink-600 mt-2 leading-relaxed">{t.body}</p>
          </div>
        ))}
      </div>

      {/* Do's & don'ts */}
      <div className="mt-9">
        <h3 className="font-serif text-xl text-ink-900 mb-4">How to get it right</h3>
        <ul className="grid gap-2.5 sm:grid-cols-2">
          {data.tips.map((tip) => {
            const dont = /^(don['’]t|never)\b/i.test(tip.trim());
            return (
              <li key={tip} className="flex gap-3 rounded-card border border-ink-200/80 bg-cream px-4 py-3.5 shadow-soft">
                <span
                  aria-hidden="true"
                  className={[
                    'shrink-0 flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold mt-0.5',
                    dont ? 'bg-ink-100 text-ink-500' : 'bg-accent-50 text-accent-600',
                  ].join(' ')}
                >
                  {dont ? '✕' : '✓'}
                </span>
                <span className="text-sm text-ink-700 leading-snug">{tip}</span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Sources */}
      <div className="mt-8 border-t border-ink-200 pt-4">
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-500">Sources</span>
        <p className="text-xs text-ink-500 mt-1.5 leading-relaxed max-w-3xl italic">{data.sourcesNote}</p>
      </div>
    </div>
  );
}

export function AiFuturePage() {
  const [track, setTrack] = useState<TrackId>('studies');

  const tabs: { id: TrackId; label: string }[] = [
    { id: 'studies', label: 'AI & your studies' },
    { id: 'careers', label: 'AI & your career' },
  ];

  return (
    <div>
      <SEO
        title="AI & Your Future — Studying and Working with AI | ACES"
        description="A balanced, student-friendly guide for commerce students on using AI to study better and on how AI is reshaping accounting, finance and business careers — synthesised from recent Anthropic and other expert research, in our own words."
        canonical="https://acescommerce.com/ai-and-you/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: 'AI & Your Future — studying and working with AI',
          about: 'How commerce students can study with AI and prepare for AI-shaped careers',
          url: 'https://acescommerce.com/ai-and-you/',
        }}
      />

      {/* ── Hero (indigo) ── */}
      <section className="aces-band-night relative overflow-hidden bg-night-700">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-60 pointer-events-none"
          style={{
            backgroundImage:
              'repeating-linear-gradient(45deg, rgba(224,123,26,0.10) 0 1px, transparent 1px 18px), repeating-linear-gradient(-45deg, rgba(224,123,26,0.10) 0 1px, transparent 1px 18px)',
            WebkitMaskImage: 'radial-gradient(120% 90% at 30% 10%, #000 0%, transparent 75%)',
            maskImage: 'radial-gradient(120% 90% at 30% 10%, #000 0%, transparent 75%)',
          }}
        />
        <div className="relative max-w-5xl mx-auto px-4 pt-16 pb-12">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-accent-200 mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-500" aria-hidden="true" />
            AI &amp; your future
          </span>
          <h1 className="font-serif text-[2.4rem] sm:text-5xl lg:text-[3.4rem] text-white tracking-tight leading-[1.06] max-w-2xl text-balance">
            Learning &amp; working in the <em className="italic text-accent-200">age of AI</em>
          </h1>
          <p className="mt-5 text-lg text-ink-100/85 max-w-2xl leading-relaxed">
            AI won’t replace commerce students who know how to use it well. Here’s an honest,
            hype-free look at how to study smarter with AI today — and how it’s reshaping the
            careers you’re working towards.
          </p>
        </div>
      </section>

      {/* ── Track toggle + content ── */}
      <section className="bg-ink-50">
        <div className="max-w-5xl mx-auto px-4 py-12">
          <div
            role="tablist"
            aria-label="Choose a topic"
            className="inline-flex rounded-full border border-ink-200/80 bg-cream p-1 shadow-soft"
          >
            {tabs.map((t) => {
              const active = track === t.id;
              return (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={active}
                  type="button"
                  onClick={() => setTrack(t.id)}
                  className={[
                    'px-4 sm:px-5 py-2 rounded-full text-sm font-semibold transition-colors duration-150',
                    active ? 'bg-accent-500 text-ink-900 shadow-btn' : 'text-ink-600 hover:text-accent-700',
                  ].join(' ')}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          <TrackView data={aiStudies} hidden={track !== 'studies'} />
          <TrackView data={aiCareers} hidden={track !== 'careers'} />
        </div>
      </section>

      {/* ── Closing CTA ── */}
      <section className="bg-cream border-t border-ink-200">
        <div className="max-w-3xl mx-auto px-4 py-14 text-center">
          <h2 className="font-serif text-2xl md:text-3xl text-ink-900 tracking-tight">
            The best preparation is still a strong foundation
          </h2>
          <p className="text-ink-600 mt-3 text-sm max-w-xl mx-auto leading-relaxed">
            AI rewards people who genuinely understand their subject. Build that understanding here —
            then let AI help you go further, not skip the thinking.
          </p>
          <Link
            to="/"
            className="aces-btn-primary mt-7"
          >
            Start learning — free →
          </Link>
        </div>
      </section>
    </div>
  );
}
